import {
  Autocomplete,
  Button,
  debounce,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { IFiltrosProjeto } from "../../../../../../application/models";
import { onlyNumbers } from "../../../../../../utils";
import { useRecortesExtracaoService } from "../../../../../../use-cases/geo-veles";
import { useAppSelector } from "../../../../../hooks/store-hooks";
import { useEffect } from "react";
import { RecorteCBO, RecorteCboFilterPreview } from "./cbo";
import { RecorteClasseSocial, RecorteClasseSocialFilterPreview } from "./classe-social";
import { RecorteContato, RecorteContatoFilterPreview } from "./contato";
import { RecorteDataNascimento, RecorteDataNascimentoFilterPreview } from "./data-nascimento";
import { RecorteEscolaridade, RecorteEscolaridadeFilterPreview } from "./escolaridade";
import { RecorteEstadoCivil, RecorteEstadoCivilFilterPreview } from "./estado-civil";
import { RecorteIdade, RecorteIdadeFilterPreview } from "./idade";
import { RecorteListaCEPFilterPreview } from "./lista-cep";
import { RecorteLocalizacao, RecorteLocalizacaoFilterPreview } from "./localizacao";
import { RecorteProfissao, RecorteProfissaoFilterPreview } from "./profissao";
import { RecorteRendaAnual, RecorteRendaAnualFilterPreview } from "./renda-anual";
import { RecorteRendaMensal, RecorteRendaMensalFilterPreview } from "./renda-mensal";
import { RecorteScore, RecorteScoreFilterPreview } from "./score";
import { RecorteSexo, RecorteSexoFilterPreview } from "./sexo";
import { RecorteListasDeduplicacao, RecorteListasDeduplicacaoFilterPreview } from "./listas-deduplicacao";
import { RecorteListasCboPreview } from "./listas-cbo/RecorteListasCboPreview";
import { RecorteListasCidadesFilterCreatePreview } from "./listas-cidade";
import { RecorteListasCbo } from "./listas-cbo/RecorteListasCbo";

const options: Array<keyof IFiltrosProjeto> = [
  "cbo",
  "listasCbo",
  "classeSocial",
  "contato",
  "dataNascimento",
  "escolaridade",
  "estadoCivil",
  "idade",
  "listasDeduplicacao",
  "localizacao",
  "profissao",
  "rendaPresumidaAnual",
  "rendaPresumidaMensal",
  "scoreVeles",
  "sexo",
];

const filterKeysToLabel = (filterKey: keyof IFiltrosProjeto): string => {
  switch (filterKey) {
    case "cbo":
      return "CBO";
    case "classeSocial":
      return "Classe Social";
    case "contato":
      return "Contatos";
    case "dataNascimento":
      return "Data de Nascimento";
    case "escolaridade":
      return "Escolaridade";
    case "estadoCivil":
      return "Estado Civil";
    case "idade":
      return "Idade";
    case "localizacao":
      return "Localização";
    case "profissao":
      return "Profissão";
    case "rendaPresumidaAnual":
      return "Renda Presumida Anual";
    case "rendaPresumidaMensal":
      return "Renda Presumida Mensal";
    case "scoreVeles":
      return "Score de Risco";
    case "sexo":
      return "Sexo";
    case "listasDeduplicacao":
      return "Listas de Deduplicação";
    case "listasCbo":
      return "Listas de CBO";
    default:
      return "";
  }
};

export const CreateRecorteProjetoForm = () => {
  // State
  const { createRecorteExtracaoForm, selectedFilter, openForm } = useAppSelector(
    (state) => state.recorteExtracao
  );
  // Hook
  const {
    setCreateRecorteFormNome,
    setCreateRecorteFormQuantidade,
    setSelectedFilter,
    resetSelectedFilter,
    resetCreateRecorteForm,
    addRecorteExtracao,
    closeEditRecorteForm,
  } = useRecortesExtracaoService();

  // Use Effect
  useEffect(() => {
    resetCreateRecorteForm();
    resetSelectedFilter();
  }, [openForm, resetCreateRecorteForm, resetSelectedFilter]);

  const isFilterValid = () => {
    const filtros = createRecorteExtracaoForm.fields.Filtros;

    // Verifica se existe pelo menos um filtro aplicado
    if (!filtros || Object.keys(filtros).length === 0) {
      return false;
    }

    // Se existe pelo menos um filtro com dados válidos, retorna true
    return Object.entries(filtros).some(([key, value]) => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }

      if (typeof value === "object" && value !== null) {
        return Object.values(value).some(
          (v) => v !== undefined && v !== null && v !== ""
        );
      }

      return value !== undefined && value !== null && value !== "";
    });
  };

  const isFormValid =
    createRecorteExtracaoForm.fields.Nome?.trim() &&
    createRecorteExtracaoForm.fields.Quantidade > 0 &&
    isFilterValid();

  // Filtragem segura das opções do Autocomplete + ordenação alfabética pelos rótulos
  const availableOptions = options
    .filter((option) => {
      const filtrosAtuais = createRecorteExtracaoForm.fields.Filtros || {};

      // Regras de exclusão mútua
      if (option === "cbo" && filtrosAtuais.profissao) return false;
      if (option === "profissao" && filtrosAtuais.cbo) return false;
      if (option === "dataNascimento" && filtrosAtuais.idade) return false;
      if (option === "idade" && filtrosAtuais.dataNascimento) return false;
      if (option === "rendaPresumidaAnual" && filtrosAtuais.rendaPresumidaMensal) return false;
      if (option === "rendaPresumidaMensal" && filtrosAtuais.rendaPresumidaAnual) return false;

      return true;
    })
    .sort((a, b) => filterKeysToLabel(a).localeCompare(filterKeysToLabel(b)));

  return (
    <>
      <Paper
        elevation={3}
        sx={{
          backgroundColor: (theme) =>
            theme.palette.mode === "light"
              ? theme.palette.grey[50]
              : theme.palette.grey[900],
          width: "100%",
          padding: 3,
        }}
      >
        <Grid container spacing={2} justifyContent="center" alignItems="center" px={1}>
          <Grid item xs={6}>
            <TextField
              onChange={debounce((e) => setCreateRecorteFormNome(e.target.value), 500)}
              error={Boolean(
                createRecorteExtracaoForm.touched &&
                createRecorteExtracaoForm.errors.find((item) => item.key === "Nome")
              )}
              helperText={
                createRecorteExtracaoForm.errors.find((item) => item.key === "Nome")?.message
              }
              name="nome"
              fullWidth
              id="nome"
              label="Dê um nome para diferenciar este recorte dos demais"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              value={
                createRecorteExtracaoForm.fields.Quantidade === 0
                  ? ""
                  : createRecorteExtracaoForm.fields.Quantidade.toLocaleString("pt-BR")
              }
              onChange={(e) => {
                const quantity = onlyNumbers(e.target.value);
                setCreateRecorteFormQuantidade(Number(quantity));
              }}
              error={Boolean(
                createRecorteExtracaoForm.touched &&
                createRecorteExtracaoForm.errors.find((item) => item.key === "Quantidade")
              )}
              helperText={
                createRecorteExtracaoForm.errors.find((item) => item.key === "Quantidade")?.message
              }
              name="quantidade"
              fullWidth
              id="quantidade"
              label="Defina a quantidade de registros que deseja exportar neste recorte"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} mt={2} textAlign="center">
            <Typography textTransform="uppercase" fontWeight="bold" variant="body1" color="primary">
              Filtros
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              value={selectedFilter || null}
              onChange={(event, newValue) => {
                setSelectedFilter(newValue as keyof IFiltrosProjeto || null);
              }}
              options={availableOptions}
              isOptionEqualToValue={(option, value) => option === value}
              getOptionLabel={(option) => filterKeysToLabel(option)}
              fullWidth
              renderInput={(params) => <TextField {...params} label="Selecione o filtro" />}
            />
          </Grid>

          <Grid container display="flex" alignItems="center" wrap="nowrap">
            {selectedFilter === "cbo" && <RecorteCBO />}
            {selectedFilter === "listasCbo" && <RecorteListasCbo />}
            {selectedFilter === "classeSocial" && <RecorteClasseSocial />}
            {selectedFilter === "contato" && <RecorteContato />}
            {selectedFilter === "dataNascimento" && <RecorteDataNascimento />}
            {selectedFilter === "escolaridade" && <RecorteEscolaridade />}
            {selectedFilter === "estadoCivil" && <RecorteEstadoCivil />}
            {selectedFilter === "idade" && <RecorteIdade />}
            {selectedFilter === "localizacao" && <RecorteLocalizacao />}
            {selectedFilter === "profissao" && <RecorteProfissao />}
            {selectedFilter === "rendaPresumidaAnual" && <RecorteRendaAnual />}
            {selectedFilter === "rendaPresumidaMensal" && <RecorteRendaMensal />}
            {selectedFilter === "scoreVeles" && <RecorteScore />}
            {selectedFilter === "sexo" && <RecorteSexo />}
            {selectedFilter === "listasDeduplicacao" && <RecorteListasDeduplicacao />}
          </Grid>

          {createRecorteExtracaoForm.fields.Filtros &&
            Object.keys(createRecorteExtracaoForm.fields.Filtros).length > 0 && (
              <>
                <Grid item xs={12} my={2} textAlign="center">
                  <Typography
                    variant="subtitle1"
                    color="primary"
                    fontWeight="bold"
                    textTransform="uppercase"
                  >
                    Resumo
                  </Typography>
                </Grid>
                <Grid
                  container
                  textAlign="left"
                  spacing={3}
                  mt={0.5}
                  justifyContent={"left"}
                  sx={{
                    margin: "0 auto",
                    paddingX: 2,
                  }}
                >

                  {/* CBOs */}
                  {createRecorteExtracaoForm.fields.Filtros.cbo && <RecorteCboFilterPreview />}
                  {createRecorteExtracaoForm.fields.Filtros.listasCbo && (
                    <RecorteListasCboPreview
                      listasCboFilterPreview={createRecorteExtracaoForm.fields.Filtros.listasCbo}
                    />
                  )}

                  {/* CLASSE SOCIAL */}
                  {createRecorteExtracaoForm.fields.Filtros.classeSocial && (
                    <RecorteClasseSocialFilterPreview />
                  )}

                  {/* CONTATO */}
                  {createRecorteExtracaoForm.fields.Filtros.contato && <RecorteContatoFilterPreview />}

                  {/* DATA DE NASCIMENTO */}
                  {createRecorteExtracaoForm.fields.Filtros.dataNascimento && (
                    <RecorteDataNascimentoFilterPreview />
                  )}

                  {/* ESCOLARIDADE */}
                  {createRecorteExtracaoForm.fields.Filtros.escolaridade && (
                    <RecorteEscolaridadeFilterPreview />
                  )}

                  {/* ESTADO CIVIL */}
                  {createRecorteExtracaoForm.fields.Filtros.estadoCivil && (
                    <RecorteEstadoCivilFilterPreview />
                  )}

                  {/* IDADE */}
                  {createRecorteExtracaoForm.fields.Filtros.idade && <RecorteIdadeFilterPreview />}

                  {/* LISTAS DE CEP */}
                  {createRecorteExtracaoForm.fields.Filtros.listasCEP && <RecorteListaCEPFilterPreview />}

                  {/* LISTAS DE DEDUPLICAÇÃO */}
                  {createRecorteExtracaoForm.fields.Filtros.listasDeduplicacao && (
                    <RecorteListasDeduplicacaoFilterPreview />
                  )}

                  {/* LOCALIZAÇÃO */}
                  {createRecorteExtracaoForm.fields.Filtros.localizacao && (
                    <RecorteLocalizacaoFilterPreview />
                  )}

                  {/* PROFISSÃO */}
                  {createRecorteExtracaoForm.fields.Filtros.profissao && <RecorteProfissaoFilterPreview />}

                  {/* RENDA PRESUMIDA ANUAL */}
                  {createRecorteExtracaoForm.fields.Filtros.rendaPresumidaAnual && (
                    <RecorteRendaAnualFilterPreview />
                  )}

                  {/* RENDA PRESUMIDA MENSAL */}
                  {createRecorteExtracaoForm.fields.Filtros.rendaPresumidaMensal && (
                    <RecorteRendaMensalFilterPreview />
                  )}

                  {/* SCORE VELES */}
                  {createRecorteExtracaoForm.fields.Filtros.scoreVeles && <RecorteScoreFilterPreview />}

                  {/* SEXO */}
                  {createRecorteExtracaoForm.fields.Filtros.sexo && <RecorteSexoFilterPreview />}

                  {/* LISTAS DE CIDADES */}
                  {createRecorteExtracaoForm.fields.Filtros.listasCidades && (
                    <RecorteListasCidadesFilterCreatePreview />
                  )}
                </Grid>
              </>
            )}

          <Grid item xs={4}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              disabled={!isFormValid}
              onClick={() => {
                addRecorteExtracao();
                closeEditRecorteForm(false);
              }}
            >
              Adicionar recorte
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};